import { UserLiteData } from '../../../api/ApiTypes';
import { StoreState } from '../../../concepts/revive/Store';
import { CoolWriteStore } from '../../packages/voting/VotingViewState';
import { Meeting } from './Meeting';
import { Phase } from './Phase';
import { SetupPhaseModel } from './SetupPhaseModel';

const meetingSetupModelTemplate = {
    mode: 'structured' as 'structured' | 'freestyle',
    welcome: '',
    startLocked: true,
    started: false,
    schedule: [false, null] as [boolean, Date | null],
    facilitators: [] as string[],
    phases: [] as SetupPhaseModel[],
};

// "default"
// "stickyTool"
// "topicTool"
// "indexCardTool"
// "tokenTool"
// "votingTool"
// "reactionTool"
// "markerTool"
// "confettiTool"
// "shapeTool"
// "textTool"
// "zoneTool"

const tools = (...list: string[]) => ({ tools: Object.fromEntries(list.map(x => [x, true])) }) as any;

export class SetupMeetingModel extends CoolWriteStore(meetingSetupModelTemplate) {

    public static createDefaultStructured(creator: UserLiteData) {
        return this.createWith({
            mode: 'structured',
            facilitators: [creator.id],
            phases: [
                SetupPhaseModel.createWith({
                    name: 'Activity #1',
                    concealment: true,
                    ...tools(
                        'default', 'stickyTool', 'topicTool', 'indexCardTool', 'roadmapCardTool', 'graphicTool',
                        'taskCardTool', 'tokenTool', 'votingTool', 'reactionTool', 'commentTool', 'connectorTool',
                        'tagTool', 'markerTool', 'pingTool', 'annotateTool', 'confettiTool', 'throwHatTool', 'objectTool'
                    )
                })
            ]
        });
    }

    public static createDefaultFreestyle(creator: UserLiteData) {
        return this.createWith({
            mode: 'freestyle',
            facilitators: [creator.id],
            startLocked: false,
            phases: [
                SetupPhaseModel.createWith({
                    name: 'Writing Phase',
                    concealment: true,
                    ...tools(
                        'default', 'stickyTool', 'topicTool', 'indexCardTool', 'roadmapCardTool', 'graphicTool',
                        'taskCardTool', 'tokenTool', 'votingTool', 'reactionTool', 'commentTool', 'connectorTool',
                        'tagTool', 'markerTool', 'pingTool', 'annotateTool', 'confettiTool', 'throwHatTool', 'objectTool'
                    ),
                    time: 10,
                })
            ]
        });
    }

    public static createWith(state: Partial<StoreState<SetupMeetingModel>>) {
        const msm = new SetupMeetingModel();
        msm.update(state);
        return msm;
    }

    public static createFromExisting(existingMeeting: Meeting) {

        const existingPhase = existingMeeting?.children?.[existingMeeting.activePhase]?.as(Phase) ?? null;
        const phases = existingMeeting.polarize(Phase).map(p => {
            const m = SetupPhaseModel.createWith({
                name: p.name,
                time: p.time / 60 / 1000,
                timeUsed: p === existingPhase ? (existingPhase.time - existingMeeting.getTimeReadingMs()) / 60 / 1000 : null,
                tools: Object.fromEntries(p.tools.map(x => [x, true])),
                active: p === existingPhase,
                incognito: p.incognito,
                concealment: p.concealment,
            });
            m.key = p.key;
            return m;
        });

        const meeting = SetupMeetingModel.createWith({
            phases,
            mode: existingMeeting.mode,
            startLocked: existingMeeting.startLocked,
            welcome: existingMeeting.welcome,
            facilitators: existingMeeting.facilitators,
            started: true,
        });

        return meeting;
    }
}
